import React, { useState, useEffect } from 'react';
import { Card, Typography, Spin, Alert, Space, Button, Input, Select } from 'antd';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { EnvironmentOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import 'leaflet/dist/leaflet.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface Claim {
  id: string;
  file_number: string;
  claimNumber: string;
  description: string;
  status: string;
  type: string;
  amount: number;
  insuredName: string;
  claimAddress?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
}

const ClaimsMap: React.FC = () => {
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadClaims();
  }, []);

  const loadClaims = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/claims?limit=1000');
      
      if (response.data.success) {
        const claimsWithCoordinates = response.data.data.filter((claim: Claim) => 
          claim.latitude && claim.longitude && 
          !isNaN(claim.latitude) && !isNaN(claim.longitude)
        );
        
        console.log(`Found ${claimsWithCoordinates.length} claims with coordinates`);
        setClaims(claimsWithCoordinates);
      } else {
        setError('Failed to load claims');
      }
    } catch (err) {
      console.error('Error loading claims:', err);
      setError('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = !searchTerm || 
      claim.file_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.claimNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.insuredName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.claimAddress?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;
    const matchesType = typeFilter === 'all' || claim.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleClaimClick = (claimId: string) => {
    navigate(`/claims/${claimId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OPEN': return 'green';
      case 'IN_PROGRESS': return 'orange';
      case 'CLOSED': return 'red';
      case 'REJECTED': return 'gray';
      default: return 'blue';
    }
  };

  const getClaimIcon = (status: string) => {
    const color = getStatusColor(status);
    return new Icon({
      iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
      shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>
          <EnvironmentOutlined className="mr-2" />
          Claims Map
        </Title>
        <Text type="secondary">
          Showing {filteredClaims.length} claims with GPS coordinates
        </Text>
      </div>

      <Card className="mb-4">
        <Space wrap>
          <Search
            placeholder="Search claims..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            placeholder="Filter by status"
          >
            <Option value="all">All Statuses</Option>
            <Option value="OPEN">Open</Option>
            <Option value="IN_PROGRESS">In Progress</Option>
            <Option value="CLOSED">Closed</Option>
            <Option value="REJECTED">Rejected</Option>
          </Select>
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 150 }}
            placeholder="Filter by type"
          >
            <Option value="all">All Types</Option>
            <Option value="PROPERTY">Property</Option>
            <Option value="LIABILITY">Liability</Option>
            <Option value="HEALTH">Health</Option>
            <Option value="AUTO">Auto</Option>
          </Select>
          <Button type="primary" icon={<FilterOutlined />} onClick={loadClaims}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        <div style={{ height: '70vh', position: 'relative' }}>
          <MapContainer
            center={[26.02631, -80.16467]}
            zoom={8}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {filteredClaims.map((claim) => (
              <Marker
                key={claim.id}
                position={[claim.latitude!, claim.longitude!]}
                icon={getClaimIcon(claim.status)}
              >
                <Popup>
                  <div style={{ minWidth: '250px' }}>
                    <Title level={5} style={{ margin: '0 0 8px 0' }}>
                      {claim.file_number}
                    </Title>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Claim:</Text> {claim.claimNumber}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Insured:</Text> {claim.insuredName}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Status:</Text> 
                      <span style={{ 
                        color: getStatusColor(claim.status), 
                        fontWeight: 'bold',
                        marginLeft: '4px'
                      }}>
                        {claim.status}
                      </span>
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Type:</Text> {claim.type}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Amount:</Text> ${claim.amount?.toLocaleString() || '0'}
                    </div>
                    {claim.claimAddress && (
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>Address:</Text> {claim.claimAddress}
                      </div>
                    )}
                    <Button 
                      type="primary" 
                      size="small"
                      onClick={() => handleClaimClick(claim.id)}
                      style={{ marginTop: '8px' }}
                    >
                      View Details
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </Card>

      <Card className="mt-4">
        <Title level={5}>Legend</Title>
        <Space wrap>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
            <div style={{ width: '16px', height: '16px', backgroundColor: 'green', marginRight: '8px', borderRadius: '2px' }}></div>
            <Text>Open</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
            <div style={{ width: '16px', height: '16px', backgroundColor: 'orange', marginRight: '8px', borderRadius: '2px' }}></div>
            <Text>In Progress</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
            <div style={{ width: '16px', height: '16px', backgroundColor: 'red', marginRight: '8px', borderRadius: '2px' }}></div>
            <Text>Closed</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
            <div style={{ width: '16px', height: '16px', backgroundColor: 'gray', marginRight: '8px', borderRadius: '2px' }}></div>
            <Text>Rejected</Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ClaimsMap; 